import { Button, Typography } from '@mui/material'
import { PlaygroundRoleProvider } from '@playground/features/auth/PlaygroundRoleContext'
import SignInScreen from '@playground/features/auth/SignInScreen'
import { useCompleteEmailLinkSignIn } from '@playground/features/auth/useCompleteEmailLinkSignIn'
import { usePlaygroundSession } from '@playground/features/auth/usePlaygroundSession'
import { getAuthInstance } from '@zealth/core/firebase/runtime'
import { signOut } from 'firebase/auth'
import React from 'react'

import CentredPanel from './CentredPanel'

/**
 * Default-deny: a session is not enough, an account also needs a role claim.
 *
 * This used to gate on "is somebody signed in", so any Firebase user of the
 * project — a mobile app user included — could reach the whole admin shell.
 * The rules denied their data; the UI did not deny them.
 */
export default function FirebaseAuthGuard({
  children,
}: {
  children: React.ReactNode
}) {
  const completing = useCompleteEmailLinkSignIn()
  const { user, role, isLoading } = usePlaygroundSession()

  if (isLoading || completing.isRunning) {
    return (
      <CentredPanel>
        <Typography>Loading...</Typography>
      </CentredPanel>
    )
  }

  if (!user) {
    return <SignInScreen error={completing.error} />
  }

  if (!role) {
    return (
      <CentredPanel>
        <Typography variant="h6" gutterBottom>
          No access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {user.email} is signed in but has no playground role. An admin has to
          grant one.
        </Typography>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => signOut(getAuthInstance())}
        >
          Sign out
        </Button>
      </CentredPanel>
    )
  }

  return (
    <PlaygroundRoleProvider
      email={user.email}
      displayName={user.displayName}
      role={role}
    >
      {children}
    </PlaygroundRoleProvider>
  )
}
